import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type {
  AssignableUser,
  CommentRequest,
  CustomerSafeTicket,
  CustomerContact,
  CustomerSafeTicketDetail,
  FormSubmitRequest,
  FormSubmitted,
  RequestType,
  RequestTypeForm,
  TicketActionsResponse,
  TicketCreated,
  TicketCreateRequest,
  TicketForm,
  TransitionApplied,
  TransitionRequest,
  WriteOk,
} from "@msc/shared";

import { api } from "../lib/api.js";
import { escapeToHtml } from "../lib/customerView/escapeHtml.js";
import { useCurrentUser } from "./useAuth.js";
import { customerViewKeys, isJiraReauthRequired } from "./useCustomerView.js";

/** Jiras Suchindex hinkt nach; sofortiges Neuladen der Liste zeigt den alten Status. */
export const JIRA_INDEX_LAG_MS = 8000;

const noRetryOnReauth = (count: number, err: unknown) => !isJiraReauthRequired(err) && count < 1;
const enc = encodeURIComponent;

export function useTicketActions(key: string | null) {
  return useQuery({
    queryKey: customerViewKeys.actions(key ?? ""),
    queryFn: () => api.get<TicketActionsResponse>(`/customer-view/tickets/${enc(key!)}/actions`),
    enabled: !!key,
    staleTime: 15_000,
    retry: noRetryOnReauth,
  });
}

export function useTicketForms(key: string | null) {
  return useQuery({
    queryKey: customerViewKeys.forms(key ?? ""),
    queryFn: () => api.get<{ forms: TicketForm[] }>(`/customer-view/tickets/${enc(key!)}/forms`),
    enabled: !!key,
    staleTime: 15_000,
    retry: noRetryOnReauth,
  });
}

export function useAssignableSearch(key: string | null, query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: customerViewKeys.assignable(key ?? "", q),
    queryFn: () => api.get<{ users: AssignableUser[] }>(`/customer-view/tickets/${enc(key!)}/assignable?query=${enc(q)}`),
    enabled: !!key && q.length >= 2,
    staleTime: 60_000,
    retry: noRetryOnReauth,
  });
}

/** Nach jeder Schreibaktion: Detail, Aktionen und Formulare sofort, die Liste nach dem Index-Nachlauf. */
function useAfterWrite(objectKey: string | null) {
  const qc = useQueryClient();
  return (key: string, opts: { listNow?: boolean } = {}) => {
    void qc.invalidateQueries({ queryKey: customerViewKeys.detail(key) });
    void qc.invalidateQueries({ queryKey: customerViewKeys.actions(key) });
    void qc.invalidateQueries({ queryKey: customerViewKeys.forms(key) });
    if (objectKey) {
      if (opts.listNow) void qc.invalidateQueries({ queryKey: customerViewKeys.tickets(objectKey) });
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: customerViewKeys.tickets(objectKey) }), JIRA_INDEX_LAG_MS);
    }
  };
}

/**
 * Statuswechsel. Die Invalidierung läuft über `onSettled` (M3), damit ein 409
 * beim Schreiben die Leseabfragen den echten Zustand zeigen lässt statt den
 * optimistischen. Zusätzlich patcht ein Erfolg die Zeile im Listen-Cache
 * sofort (S2) — die verzögerte Invalidierung in `after()` bleibt als
 * Wahrheitsabgleich mit Jiras nachhinkendem Suchindex.
 */
export function useTransitionTicket(objectKey: string | null) {
  const after = useAfterWrite(objectKey);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, toStatusCategory: _toStatusCategory, ...body }: { key: string; toStatusCategory: string } & TransitionRequest) =>
      api.post<TransitionApplied>(`/customer-view/tickets/${enc(key)}/transition`, body),
    onSuccess: (result, v) => {
      if (!objectKey) return;
      qc.setQueryData<CustomerSafeTicket[]>(customerViewKeys.tickets(objectKey), (old) =>
        old?.map((ticket) =>
          ticket.key === v.key ? { ...ticket, status: result.toStatus, statusCategory: v.toStatusCategory } : ticket,
        ),
      );
    },
    onSettled: (_r, _e, v) => after(v.key),
  });
}

/**
 * Kommentar schreiben — und ihn SOFORT in den Detail-Cache setzen.
 *
 * Der Grund ist die Bühne: Ihr Schnappschuss liest die Kommentare des
 * laufenden Tickets aus `detail.data.comments`. Bis Jira den frisch
 * geschriebenen Kommentar zurückliefert, vergeht im Termin spürbar Zeit — der
 * Lead sagt „hab ich gerade eingetragen", und auf der Leinwand steht nichts.
 * Deshalb steht er hier optimistisch im Cache: ein kundensichtbarer Kommentar
 * ist damit im nächsten Schnappschuss auf der Bühne, ein interner in der
 * Spalte „Nur für dich" und dort freigebbar.
 *
 * Die Sicherheitsinvariante bleibt unberührt — der Eintrag trägt sein
 * `isPublic` aus der GESCHRIEBENEN Sichtbarkeit, und auf die Bühne kommt er
 * wie jeder andere ausschließlich über `buildStageSnapshot`.
 *
 * `onSettled: after(...)` bleibt: Die echten Jira-Daten ersetzen den
 * optimistischen Eintrag, sobald sie da sind. Er ist ein Platzhalter, keine
 * zweite Wahrheit — deshalb auch der escapte Klartext als `bodyHtml` statt
 * eines Nachbaus dessen, was Jiras Renderer daraus macht.
 */
export function useAddComment(objectKey: string | null) {
  const after = useAfterWrite(objectKey);
  const qc = useQueryClient();
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  // Unter „Sicht übernehmen" ist `fullName` die ÜBERNOMMENE Person, während Jira
  // den Kommentar dem tatsächlichen OAuth-Token-Inhaber zuschreibt — ein
  // optimistischer Anzeigename kann daher kurzzeitig danebenliegen; er
  // korrigiert sich mit dem nächsten Refetch (`after(...)` unten) von selbst.
  const authorName = user?.fullName?.trim() || t("pages.customerView.present.you");
  return useMutation({
    mutationFn: ({ key, ...body }: { key: string } & CommentRequest) =>
      api.post<{ commentId: string }>(`/customer-view/tickets/${enc(key)}/comment`, body),
    onSuccess: (result, v) => {
      qc.setQueryData<CustomerSafeTicketDetail>(customerViewKeys.detail(v.key), (old) =>
        old
          ? {
              ...old,
              comments: [
                ...old.comments,
                {
                  id: result.commentId,
                  authorName,
                  createdIso: new Date().toISOString(),
                  bodyHtml: escapeToHtml(v.text),
                  isPublic: v.visibility === "public",
                },
              ],
            }
          : old,
      );
    },
    onSettled: (_r, _e, v) => after(v.key, { listNow: true }),
  });
}

export function useSetDueDate(objectKey: string | null) {
  const after = useAfterWrite(objectKey);
  return useMutation({
    mutationFn: ({ key, dueDate }: { key: string; dueDate: string | null }) =>
      api.put<WriteOk>(`/customer-view/tickets/${enc(key)}/duedate`, { dueDate }),
    onSettled: (_r, _e, v) => after(v.key, { listNow: true }),
  });
}

export function useSetPriority(objectKey: string | null) {
  const after = useAfterWrite(objectKey);
  return useMutation({
    mutationFn: ({ key, priorityId }: { key: string; priorityId: string }) =>
      api.put<WriteOk>(`/customer-view/tickets/${enc(key)}/priority`, { priorityId }),
    onSettled: (_r, _e, v) => after(v.key, { listNow: true }),
  });
}

export function useSetAssignee(objectKey: string | null) {
  const after = useAfterWrite(objectKey);
  return useMutation({
    mutationFn: ({ key, accountId }: { key: string; accountId: string | null }) =>
      api.put<WriteOk>(`/customer-view/tickets/${enc(key)}/assignee`, { accountId }),
    onSettled: (_r, _e, v) => after(v.key, { listNow: true }),
  });
}

export function useSubmitForm(objectKey: string | null) {
  const after = useAfterWrite(objectKey);
  return useMutation({
    mutationFn: ({ key, formId, answers }: { key: string; formId: string } & FormSubmitRequest) =>
      api.put<FormSubmitted>(`/customer-view/tickets/${enc(key)}/forms/${enc(formId)}`, { answers }),
    onSettled: (_r, _e, v) => after(v.key),
  });
}

/** Anfragetypen des Kundenportals (#271) — ändern sich selten. */
export function useRequestTypes(enabled: boolean) {
  return useQuery({
    queryKey: customerViewKeys.requestTypes,
    queryFn: () => api.get<{ requestTypes: RequestType[] }>("/customer-view/request-types"),
    enabled,
    staleTime: 10 * 60_000,
    retry: noRetryOnReauth,
  });
}

export function useRequestTypeForm(id: string | null) {
  return useQuery({
    queryKey: customerViewKeys.requestTypeForm(id ?? ""),
    queryFn: () => api.get<RequestTypeForm>(`/customer-view/request-types/${enc(id!)}/fields`),
    enabled: !!id,
    staleTime: 10 * 60_000,
    retry: noRetryOnReauth,
  });
}

export function useCustomerContacts(objectKey: string, enabled: boolean) {
  return useQuery({
    queryKey: customerViewKeys.contacts(objectKey),
    queryFn: () => api.get<{ contacts: CustomerContact[] }>(`/customer-view/customers/${enc(objectKey)}/contacts`),
    enabled,
    staleTime: 60_000,
    retry: noRetryOnReauth,
  });
}

/**
 * Neues Ticket (#271). Die Liste lädt sofort und nach dem Index-Nachlauf neu —
 * ein frisch angelegter Vorgang steht erst dann in der Suche, und erst wenn
 * Jira ihm die Kundenabteilung gegeben hat.
 */
export function useCreateTicket(objectKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TicketCreateRequest) =>
      api.post<TicketCreated>(`/customer-view/customers/${enc(objectKey)}/tickets`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerViewKeys.tickets(objectKey) });
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: customerViewKeys.tickets(objectKey) }), JIRA_INDEX_LAG_MS);
      void qc.invalidateQueries({ queryKey: customerViewKeys.contacts(objectKey) });
    },
  });
}
